<template>
  <div id="dashboardAside">
    <div
      class="appName"
      :class="{
        themeLogo: appName === 'GCG Platform' && (theme && theme.logo),
      }"
    >
      <!-- GCG Platform -->
      <el-image
        v-if="appName === 'GCG Platform' && (theme && theme.logo)"
        :src="theme.logo"
        fit="contain"
        style="height: 40px; width: 175px;"
      >
        <div
          slot="placeholder"
          class="image-slot"
        >
        </div>
      </el-image>
      <span
        v-else
        style="position: relative; top: 15px;"
      >
        {{ appName }}
      </span>
    </div>

    <div class="navBar">
      <el-menu
        :default-active="defaultActive"
        class="el-menu-vertical"
        text-color="var(--themeFontColor)"
        active-text-color="var(--themeFontColor)"
        background-color="#00000000"
        @open="openSubMenu"
        @close="openSubMenu"
      >
        <div
          v-for="(item, key) in structuredItems"
          :key="key"
        >
          <el-menu-item
            v-if="!item.isParent"
            :index="item.index"
            :route="item.route"
            :disabled="item.disabled"
            @click="handleClick"
          >
            <span>{{ $t(item.label) }}</span>
          </el-menu-item>

          <el-submenu
            v-else
            :index="item.index"
            :class="{isActive: defaultActive === `${item.index}-0`}"
          >
            <template slot="title">
              <span>{{ $t(`aside.${item.label}`) }}</span>
            </template>
            <el-menu-item
              v-for="(subItem, subKey) in item.children"
              :key="subKey"
              class="subMenu"
              :index="subItem.index"
              :route="subItem.route"
              :disabled="subItem.disabled"
              @click="handleClick"
            >
              {{ $t(subItem.label) }}
            </el-menu-item>
          </el-submenu>
        </div>
      </el-menu>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';

export default {
  data() {
    return {
      defaultActive: '1',
    };
  },

  computed: {
    ...mapState({
      user: (state) => state.user,
      userRole: (state) => state.user.user.role_type,
      isAC: (state) => state.user.profile.is_ac,
      currentSession: (state) => state.processStatus.process.session,
      hasOtherEngagement: (state) => state.processStatus.process.has_other_engagements,
    }),

    ...mapGetters('myCoach', {
      hasCoach: 'hasCoach',
    }),

    ...mapGetters('user', {
      theme: 'getTheme',
    }),

    env() {
      return process.env.VUE_APP_ENV;
    },

    appName() {
      return process.env.VUE_APP_APP_NAME ? process.env.VUE_APP_APP_NAME : 'GCG Platform';
    },

    filteredItems() {
      // resolve doesn't expose children route
      // const dashboard = this.$router.resolve({ name: 'dashboard' });
      const dashboard = this.$router.options.routes.find((el) => el.path === '/dashboard');

      if (!dashboard) return [];

      const items = dashboard.children.filter((route) => {
        // in the item with nav menu options,
        // we keep the ones with no role restriction or with allowed roles
        if (route.name === 'engagementsList') {
          if (this.hasOtherEngagement && route.meta.roles.includes(this.userRole)) {
            return true;
          }
          return false;
        }

        if (route.meta && route.meta.navMenuOptions) {
          if (!route.meta.roles || route.meta.roles.includes(this.userRole)) {
            return true;
          }
        }
        return false;
      });
      return items;
    },

    structuredItems() {
      const formattedItems = {};

      this.filteredItems.forEach((el) => {
        // if index is something like 20-0, we don't display in menu
        // stand alone option can make menu exist itself even without children
        // since it s a "main page" of submenu
        if (el.meta.navMenuOptions.index
          && el.meta.navMenuOptions.index.split('-')[1] === '0'
          && !el.meta.navMenuOptions.standAlone) {
          return;
        }

        if (this.userRole === 'coach' && !this.isAC && el.meta.navMenuOptions.hideForNonAc) {
          return;
        }

        if (this.userRole === 'OA' && el.meta.navMenuOptions.cobrandingCheck) {
          if (!(this.user && this.user.organization && this.user.organization.allow_cobranding)) {
            return;
          }
        }

        if (el.meta.navMenuOptions.status
          && ['dev', 'staging'].includes(el.meta.navMenuOptions.status)) {
          // Dev router filter
          if (el.meta.navMenuOptions.status === 'dev') {
            if (['dev', 'local'].includes(this.env)) {
              console.log('# Only shown in DEV!', el.name);
            } else {
              return;
            }
          } else if (el.meta.navMenuOptions.status === 'staging') {
            if (['dev', 'local', 'staging'].includes(this.env)) {
              console.log('# Only shown in DEV, STAGING!', el.name);
            } else {
              return;
            }
          }
        }

        const { index } = el.meta.navMenuOptions;
        if (!el.meta.navMenuOptions.parent) {
          if (index) {
            formattedItems[index] = {
              index,
              label: el.meta.navMenuOptions.label,
              route: { name: el.name },
              isParent: false,
            };

            if (el.meta.minSession) {
              formattedItems[index].disabled = el.meta.minSession > this.currentSession;
            }

            if (el.meta.navMenuOptions.status) {
              formattedItems[index].disabled = el.meta.navMenuOptions.status === 'closed';
            }

            if (el.name === 'schedule' && !this.hasCoach) {
              formattedItems[index].disabled = true;
            }
          }
        } else {
          const parentIndex = index.split('-')[0];
          if (!formattedItems[parentIndex]) {
            formattedItems[parentIndex] = {
              index: parentIndex,
              isParent: true,
              label: el.meta.navMenuOptions.parent,
              children: {},
            };
          }

          const item = {
            index,
            label: el.meta.navMenuOptions.label,
            route: { name: el.name },
          };

          if (el.meta.minSession) {
            item.disabled = el.meta.minSession > this.currentSession;
          }

          if (el.meta.navMenuOptions.status) {
            item.disabled = el.meta.navMenuOptions.status === 'closed';
          }

          formattedItems[parentIndex].children[index] = item;
        }
      });

      // If stand alone menu have some children at current role, remain only submenu
      Object.keys(formattedItems).forEach((item) => {
        const num = formattedItems[item].index;
        if (num.split('-')[1] * 1 === 0 && formattedItems[num.split('-')[0]]) {
          delete formattedItems[item];
        }
      });

      return formattedItems;
    },
  },

  watch: {
    $route(to) {
      const el = this.filteredItems.find((item) => item.name === to.name);
      // this.defaultActive = el ? el.meta.navMenuOptions.index : '';
      if (el && el.meta.navMenuOptions.index) {
        this.defaultActive = el.meta.navMenuOptions.index;
        return;
      }
      if (el && el.meta.navMenuOptions.as) {
        const alias = this.filteredItems.find((item) => item.name === el.meta.navMenuOptions.as);
        if (alias && alias.meta.navMenuOptions.index) {
          this.defaultActive = alias.meta.navMenuOptions.index;
          return;
        }
      }
      this.defaultActive = '';
    },
  },

  created() {
    const el = this.filteredItems.find((item) => item.name === this.$route.name);
    if (el && el.meta.navMenuOptions.index) {
      this.defaultActive = el.meta.navMenuOptions.index;
      return;
    }
    if (el && el.meta.navMenuOptions.as) {
      const alias = this.filteredItems.find((item) => item.name === el.meta.navMenuOptions.as);
      if (alias && alias.meta.navMenuOptions.index) {
        this.defaultActive = alias.meta.navMenuOptions.index;
        return;
      }
    }
    this.defaultActive = '';
  },

  methods: {
    openSubMenu(index) {
      const mainPage = this.filteredItems.find(
        (el) => el.meta.navMenuOptions.index === `${index}-0`,
      );
      if (mainPage && mainPage.name !== this.$router.currentRoute.name) {
        this.$router.push({ name: mainPage.name }).catch((err) => {});
      }
    },

    handleClick(item) {
      this.$router.push(item.route).catch((err) => {});
    },
  },
};
</script>

<style lang="scss">
#dashboardAside {
  position: relative;

  .subMenu {
    padding: 0 26px !important;
  }

  .appName {
    position: absolute;
    width: 175px;
    height: 70px;
    left: 23px;
    top: 20px;
    font-style: normal;
    font-weight: bold;
    line-height: normal;
    font-size: 16px;
    color: var(--themeFontColor);

    &.themeLogo {
      display: flex;
      justify-content: center;
      align-items: center;
      margin-top: -5px;
    }
  }

  .navBar {
    position: relative;
    top: 100px;
    text-align: left;

    .el-menu {
      background-color: inherit;
      border-width: 0;

      .el-submenu__title,
      .el-menu-item {
        background-color: rgba(255, 255, 255, 0) !important;

        &:hover {
          background-color: rgba(255, 255, 255, 0.1) !important;
        }

        &.is-active {
          font-weight: bold;
          background-color: var(--themeCardColor) !important;
        }

        .el-submenu__icon-arrow {
          color: var(--themeFontColor);
        }
      }

      .el-submenu.isActive .el-submenu__title {
        background-color: var(--themeCardColor) !important;
      }
    }
  }
}
</style>
